import { useCallback, useRef, useState } from "react";
import { uuidv7 } from "uuidv7";
import { pluralizeWithCount } from "@common/helpers/string";
import { MonthRecurrenceType } from "@common/types/serviceTemplateRecurrence";
import type { CloseTemplate } from "@common/types/closeTemplate";
import { useCreateServiceTemplateScheduleMutation } from "@api/serviceTemplateSchedule.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type { ScheduleDefaults } from "./applyCloseTemplate.schema";
import type { SelectedEntityEntry } from "./ApplyCloseTemplateStepReview";

interface Params {
    organizationId: string | null | undefined;
    closeTemplate: CloseTemplate | undefined;
    schedule: ScheduleDefaults | undefined;
    onAllCreated: () => void;
}

/** Bulk-creates one schedule per selected entity, looping the single-entity create mutation (mirrors `handleApplyAll`'s per-entity loop for the apply variant). */
export function useCreateSchedulesForEntities({
    organizationId,
    closeTemplate,
    schedule,
    onAllCreated,
}: Params) {
    const { toast: successToast, error: errorToast } = useToaster();
    const createMutation = useCreateServiceTemplateScheduleMutation();

    const [createdEntityIds, setCreatedEntityIds] = useState(
        () => new Set<number>(),
    );
    const [creatingEntityId, setCreatingEntityId] = useState<number | null>(
        null,
    );
    const [isCreatingBulk, setIsCreatingBulk] = useState(false);
    const isCreatingBulkRef = useRef(false);
    // Generated once per bulk-create flow and reused across retries so that a
    // partial-failure retry persists the remaining schedules under the same
    // group as the ones that already succeeded, instead of minting a fresh id.
    const groupIdRef = useRef<string | null>(null);

    const createSchedules = useCallback(
        async (entries: SelectedEntityEntry[]) => {
            // `period` is required by the create endpoint but nullable in
            // form state until the user picks one on step 1 — the wizard's
            // own step validation blocks continuing without it, so this is
            // just a type-narrowing guard, not a reachable user-facing path.
            if (
                !closeTemplate ||
                !schedule ||
                !schedule.period ||
                !organizationId
            ) {
                return;
            }
            if (isCreatingBulkRef.current) {
                return;
            }

            const { period } = schedule;
            isCreatingBulkRef.current = true;
            setIsCreatingBulk(true);
            const createForEntries = async () => {
                let succeededEntities = 0;
                let failedEntities = 0;
                let skippedEntities = 0;
                let firstFailureMessage: string | null = null;
                const pending = entries.filter(
                    (e) => !createdEntityIds.has(Number(e.entityIdStr)),
                );
                // A single groupId links all schedules created in this batch
                // together, even though each entity's create call is a separate
                // request. Only meaningful when creating for more than one
                // entity — a lone schedule doesn't need to be grouped. Keyed off
                // the full batch (`entries`), not `pending`, and cached in a ref
                // so retries after a partial failure keep the original group.
                if (entries.length > 1 && groupIdRef.current === null) {
                    groupIdRef.current = uuidv7();
                }
                const groupId = groupIdRef.current ?? undefined;
                for (const { entityIdStr, row } of pending) {
                    const { entityUuid } = row;
                    // Rows without a resolved entity uuid can't be scheduled
                    // (e.g. the frontend is briefly ahead of the backend uuid
                    // projection during a deploy). Track them so we can surface
                    // an error and avoid closing the wizard as if it succeeded.
                    if (!entityUuid) {
                        skippedEntities += 1;
                        continue;
                    }
                    try {
                        const entityId = Number(entityIdStr);
                        setCreatingEntityId(entityId);
                        await createMutation.mutateAsync({
                            organizationId,
                            body: {
                                templateId: closeTemplate.id,
                                entityUuid,
                                groupId,
                                workspaceId: row.workspaceId,
                                period,
                                interval: schedule.interval,
                                startDate: schedule.startDate,
                                endDate: schedule.endDate ?? undefined,
                                maxOccurrenceCount:
                                    schedule.maxOccurrenceCount ?? undefined,
                                applicationLeadValue:
                                    schedule.applicationLeadValue,
                                applicationLeadUnit:
                                    schedule.applicationLeadUnit,
                                weekDays:
                                    schedule.weekDays.length > 0
                                        ? schedule.weekDays
                                        : undefined,
                                datesOfMonth:
                                    schedule.datesOfMonth.length > 0
                                        ? schedule.datesOfMonth
                                        : undefined,
                                dateOfMonth: schedule.dateOfMonth ?? undefined,
                                monthRecurrenceType:
                                    schedule.dateOfMonth != null
                                        ? MonthRecurrenceType.DAY
                                        : undefined,
                                month: schedule.month ?? undefined,
                                quarterMonth:
                                    schedule.quarterMonth ?? undefined,
                                workStartDateMode: schedule.workStartDateMode,
                                workStartOffsetDays:
                                    schedule.workStartOffsetDays ?? undefined,
                                workStartSkipWeekend:
                                    schedule.workStartSkipWeekend,
                                workStartWeekday:
                                    schedule.workStartWeekday ?? undefined,
                                workEndDateMode: schedule.workEndDateMode,
                                workEndOffsetDays:
                                    schedule.workEndOffsetDays ?? undefined,
                                workEndRelativePeriod:
                                    schedule.workEndRelativePeriod ?? undefined,
                                workEndRelativePosition:
                                    schedule.workEndRelativePosition ??
                                    undefined,
                                workEndRelativeDayType:
                                    schedule.workEndRelativeDayType ??
                                    undefined,
                                workEndRelativeDayOfPeriod:
                                    schedule.workEndRelativeDayOfPeriod ??
                                    undefined,
                                preparerId: row.preparerId ?? undefined,
                                reviewerId: row.reviewerId ?? undefined,
                                managerId: row.managerId ?? undefined,
                            },
                        });
                        succeededEntities += 1;
                        setCreatedEntityIds((prev) =>
                            new Set(prev).add(entityId),
                        );
                    } catch (err) {
                        failedEntities += 1;
                        const message =
                            err instanceof Error
                                ? err.message
                                : "Failed to create schedule";
                        firstFailureMessage = firstFailureMessage ?? message;
                    }
                }
                if (succeededEntities > 0) {
                    successToast(
                        `Created ${pluralizeWithCount("schedule", succeededEntities)}`,
                    );
                }
                if (failedEntities > 0) {
                    const failedBaseMessage = `${pluralizeWithCount("entity", failedEntities)} failed`;
                    errorToast(
                        firstFailureMessage
                            ? `${failedBaseMessage}: ${firstFailureMessage}`
                            : failedBaseMessage,
                    );
                }
                if (skippedEntities > 0) {
                    errorToast(
                        `Skipped ${pluralizeWithCount("entity", skippedEntities)} that couldn't be scheduled`,
                    );
                }
                if (failedEntities === 0 && skippedEntities === 0) {
                    onAllCreated();
                }
            };
            await createForEntries().finally(() => {
                isCreatingBulkRef.current = false;
                setIsCreatingBulk(false);
                setCreatingEntityId(null);
            });
        },
        [
            closeTemplate,
            createMutation,
            createdEntityIds,
            errorToast,
            onAllCreated,
            organizationId,
            schedule,
            successToast,
        ],
    );

    return {
        createSchedules,
        createdEntityIds,
        creatingEntityId,
        isCreatingBulk,
    };
}
